/**
 * AppUpdateBanner — banner-center source (deploy-freshness doctrine,
 * 2026-08-20 owner directive: users must never need to remember a hard
 * refresh; a calm banner offers the new version, one click brings them
 * fully up to date, and they land exactly where they were).
 *
 * How it knows a new version shipped: it polls /asset-manifest.json (the
 * CRA build manifest — both deploy lanes serve it with Cache-Control:
 * no-cache, so the edge never pins it) and compares files["main.js"], a
 * content-hashed path that changes on every meaningful deploy, against the
 * value captured on first load. No build-pipeline stamp needed.
 *
 * The experience contract:
 *   - ZERO disruption until clicked — never auto-reloads, never interrupts.
 *   - "Exactly where they were" is guaranteed by the existing persistence
 *     layer: window.location.reload() keeps path/query/hash, the active
 *     thread + thread list restore from their uid-scoped localStorage keys,
 *     chat open/dock/fullscreen restore synchronously in the reducer
 *     initializer, and the composer draft survives. Server-side tasks
 *     re-attach on hydrate (taskRecovery.resumeQueuedTaskOnHydrate).
 *   - SHOWN ONLY WHEN READY (2026-09-02 owner directive — the greyed-out
 *     "waits for your answer…" pill is retired): the banner never renders a
 *     disabled control. While any tab is mid-answer (the cross-tab lease in
 *     streamingFlag.ts, derived from the send path's own processing flags
 *     and queued sends) the offer is simply HELD BACK; it appears the moment
 *     the answer lands, always with a live "refresh now". A user is never
 *     shown a button they cannot press.
 *   - ONCE SHOWN, IT STAYS CLICKABLE — even if a new answer starts. The
 *     click always reloads; nothing greys out mid-conversation. Task-backed
 *     turns re-attach after the reload; an inline turn caught mid-stream
 *     surfaces the interrupted card with its one-tap Regenerate.
 *   - The hold can never become a dead end (2026-08-25 owner report: a leaked
 *     flag once wedged the old softened pill indefinitely). If an offer has
 *     been held back for READY_FAIL_OPEN_MS in total — however the lease
 *     flickers — the banner shows anyway: past that point the user's choice
 *     outranks a signal far more likely stuck than real.
 *   - Dismissal snoozes THIS offered version for the session; a newer
 *     deploy re-offers.
 *   - The one scoped exception to "never auto-reloads" lives in
 *     src/utils/staleTabSelfHeal.ts: a silent recovery reload when the
 *     running code is provably at risk of dead-ending (pruned assets /
 *     exhausted chunk failure / retention-coupled boot age) — never for a
 *     mere update.
 *
 * Poll cadence: 60s (lighter than the 20s /desktop convention — deploys
 * are minutes apart at best) + an immediate check when the tab regains
 * visibility (the EmailVerifyBanner pattern). Fetch failures are silent:
 * the global fetch patch (src/index.tsx) masks non-API failures into a
 * synthetic 500 Response, so we gate on resp.ok + JSON parse, never on
 * promise rejection.
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BannerBusContext, useRegisterBanner, BannerDescriptor } from './GlobalBannerCenter';
// The lease module directly (not the giant chat hook) — keeps this banner's
// import graph tiny and its tests mock-free.
import {
  isAnyTabStreaming,
  STREAMING_FLAG_FRESHNESS_MS,
} from '../GlobalChatSystem/streamingFlag';
// Pure progress bookkeeping (no Firebase): the same reader the uploader feeds.
import { isAnyUploadMoving } from '../../utils/attachmentUploadProgress';

export const UPDATE_POLL_INTERVAL_MS = 60_000;
/** Re-check the lease this often while an update is on offer but held back. */
export const STREAMING_RECHECK_MS = 5_000;
/** One window for every reader of the lease — defined beside the writer. */
export const STREAMING_FRESHNESS_WINDOW_MS = STREAMING_FLAG_FRESHNESS_MS;
/**
 * Fail-open cap on holding an offer back: once an update has been held for
 * this long IN TOTAL (accumulated across lease flickers — a streak that
 * restarts on every clear read could be starved forever), it is shown anyway.
 * Comfortably above the longest real inline turns (deep research runs
 * 5-8 min); a lease fresh beyond it is overwhelmingly a stuck signal.
 */
export const READY_FAIL_OPEN_MS = 600_000;
/** @deprecated alias kept for the self-heal module's mirrored constant. */
export const SOFTEN_FAIL_OPEN_MS = READY_FAIL_OPEN_MS;
export const UPDATE_SNOOZE_KEY = 'prest0_update_snoozed_main';
const MANIFEST_URL = '/asset-manifest.json';

const RefreshButton = styled.button`
  flex: none;
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: #8a6b2a;
  border: none;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(1.08); }
`;

/** The live bundle path from a fetched manifest, or null on any weirdness.
 * Exported for staleTabSelfHeal, which reuses this exact SPA-fallback-safe
 * probe; the '/asset-manifest.json' literal must stay in THIS file
 * (check-cache-doctrine pins the oracle and the banner together). */
export async function fetchLiveMainJs(): Promise<string | null> {
  try {
    const resp = await fetch(`${MANIFEST_URL}?ts=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as { files?: Record<string, string> };
    const main = data?.files?.['main.js'];
    // SPA-fallback safety: a rewrite serving index.html here would fail the
    // JSON parse above; a parse that "succeeds" without files.main.js is
    // equally untrustworthy. Only a hashed-looking bundle path counts.
    return typeof main === 'string' && /main\.[a-zA-Z0-9]+\.js/.test(main) ? main : null;
  } catch {
    return null;
  }
}

/** Is any tab mid-answer right now? One reader, one module — no local copy. */
export function isAnswerStreaming(nowMs: number = Date.now()): boolean {
  return isAnyTabStreaming(nowMs);
}

export const AppUpdateBanner: React.FC = () => {
  const bus = useContext(BannerBusContext);
  const baselineRef = useRef<string | null>(null);
  const [offeredMain, setOfferedMain] = useState<string | null>(null);
  const [snoozedMain, setSnoozedMain] = useState<string | null>(() => {
    try { return sessionStorage.getItem(UPDATE_SNOOZE_KEY); } catch { return null; }
  });
  // The version the banner is currently SHOWING (null while an offer is held
  // back behind a live answer). Sticky: once shown, a new answer starting does
  // not hide it again — and a NEWER deploy landing while it is visible simply
  // takes over the offer (review finding, 2026-09-02: re-gating the new version
  // hid an already-clickable banner for up to the fail-open budget).
  const [shownMain, setShownMain] = useState<string | null>(null);
  // Ref mirrors so the readiness effect reads live values without re-arming
  // its ticker on every state change.
  const shownRef = useRef<string | null>(null);
  const snoozedRef = useRef<string | null>(snoozedMain);
  useEffect(() => { shownRef.current = shownMain; }, [shownMain]);
  useEffect(() => { snoozedRef.current = snoozedMain; }, [snoozedMain]);

  // Version watch: capture the served bundle on first success, then compare
  // on every poll / tab return. Baseline-from-network (not from the running
  // bundle) means a user who loaded mid-deploy converges on the next poll.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const live = await fetchLiveMainJs();
      if (cancelled || !live) return;
      if (baselineRef.current === null) {
        baselineRef.current = live;
        return;
      }
      if (live !== baselineRef.current) {
        setOfferedMain(live);
      }
    };
    void check();
    const interval = setInterval(() => void check(), UPDATE_POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Readiness gate: while an offer is pending and not yet shown, re-check the
  // lease every STREAMING_RECHECK_MS and show the banner the moment no answer
  // is mid-flight — or once the offer has been held back for the fail-open
  // budget in total. Held time accumulates per tick, so a lease that flickers
  // cannot restart the clock.
  useEffect(() => {
    if (!offeredMain) return;
    // A banner is already on screen for an earlier version (shown, not snoozed):
    // the newer version takes over immediately. Only the FIRST offer waits on
    // readiness — a visible, clickable offer is never taken away.
    if (shownRef.current !== null && shownRef.current !== snoozedRef.current) {
      setShownMain(offeredMain);
      return;
    }
    let heldMs = 0;
    let lastTick = Date.now();
    const evaluate = () => {
      const now = Date.now();
      const streaming = isAnswerStreaming(now);
      if (streaming) heldMs += now - lastTick;
      lastTick = now;
      // A file upload under way in this tab holds the offer too (2026-10-08):
      // refreshing would end it, and a large one runs for an hour. It spends
      // none of the fail-open budget, because it cannot be a stuck signal —
      // "moving" means bytes went out in the last few minutes.
      if (isAnyUploadMoving(now)) return false;
      if (!streaming || heldMs >= READY_FAIL_OPEN_MS) {
        setShownMain(offeredMain);
        return true;
      }
      return false;
    };
    if (evaluate()) return;
    const tick = setInterval(() => {
      if (evaluate()) clearInterval(tick);
    }, STREAMING_RECHECK_MS);
    return () => clearInterval(tick);
  }, [offeredMain]);

  const handleRefresh = () => {
    // Route (path/query/hash), active thread, chat layout, and composer
    // draft all restore from their persisted keys — no marker needed. The
    // click ALWAYS lands: the banner is only ever shown when it is safe, and
    // once shown the user's explicit choice outranks a later-starting answer.
    window.location.reload();
  };

  const handleDismiss = () => {
    if (!offeredMain) return;
    try { sessionStorage.setItem(UPDATE_SNOOZE_KEY, offeredMain); } catch { /* ignore */ }
    setSnoozedMain(offeredMain);
  };

  const eligible = !!offeredMain && offeredMain !== snoozedMain && shownMain === offeredMain;

  const descriptor: BannerDescriptor | null = eligible
    ? {
        id: 'app-update',
        tone: 'info',
        dismissible: true,
        onDismiss: handleDismiss,
        // The phone line (2026-09-13). The reassurance — "you'll come back
        // right where you are" — is the half that can wait for a tap: the
        // reader's decision is only whether to refresh, and the button says
        // that. House voice, lowercase.
        shortContent: (
          <>
            <span data-testid="app-update-copy-short">a new version of prest0 is ready.</span>
            <RefreshButton
              type="button"
              onClick={handleRefresh}
              data-testid="app-update-refresh-short"
            >
              refresh
            </RefreshButton>
          </>
        ),
        content: (
          <>
            <span data-testid="app-update-copy">
              prest0 just got better — refresh to pick up the latest. you'll come back
              right where you are.
            </span>
            <RefreshButton
              type="button"
              onClick={handleRefresh}
              data-testid="app-update-refresh"
            >
              refresh now
            </RefreshButton>
          </>
        ),
      }
    : null;

  useRegisterBanner(bus ? descriptor : null);

  // Banner-center-native: outside the center it renders nothing.
  return null;
};

export default AppUpdateBanner;
